import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '../../../../auth/service';
import { Fournisseur } from '../../../../models/fournisseur';
import {FournisseurService} from '../../../../services/Fournisseur/fournisseur.service';

@Component({
    selector: 'app-add-fourniss',
    templateUrl: './add-fourniss.component.html',
    styleUrls: ['./add-fourniss.component.scss']
})
export class AddFournissComponent implements OnInit {
    @Input() clientId!: number;
    @Input() stationId!: number;
    @Input() projectData: any = null;
    @Output() fournisseurSaved = new EventEmitter<Fournisseur>();
    @ViewChild('modalLG') modalTemplate?: TemplateRef<any>;

    private options: GlobalConfig;

    fournisseur: Partial<Fournisseur> = {
        civilite: '',
        nom_entreprise: '',
        numero_identification_fiscale: '',
        identifiant_unique: '',
        site_web: '',
        station_id: 0,
        user_id: 0,
        emails: [],
        telephones: [],
        adresses: []
    };

    modalRef: NgbModalRef | null = null;

    constructor(
        private toastr: ToastrService,
        private modalService: NgbModal,
        private fournisseurService: FournisseurService,
        private authService: AuthenticationService
    ) {
        this.options = this.toastr.toastrConfig;
    }

    ngOnInit(): void {
        this.fournisseur.station_id = this.stationId;
        this.fournisseur.user_id = this.authService.currentUserValue?.id || 0;
    }

    openModal(): void {
        this.resetForm();
        const options: NgbModalOptions = {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            size: 'lg'
        };
        this.modalRef = this.modalService.open(this.modalTemplate, options);
    }

    get isFormValid(): boolean {
        return !!(
            this.fournisseur.civilite &&
            this.fournisseur.nom_entreprise?.trim() &&
            this.fournisseur.numero_identification_fiscale?.trim() &&
            this.fournisseur.identifiant_unique?.trim()
        );
    }

    /** Normalise la civilité pour l'API : retourne "Homme" ou "Femme" (valeurs attendues par l'API). */
    private normalizeCivilite(value: string | undefined): string {
        if (!value || typeof value !== 'string') return value || '';
        const v = value.trim().toLowerCase();
        if (v === 'avant' || v === 'homme') return 'Homme';
        if (v === 'apres' || v === 'après' || v === 'femme') return 'Femme';
        if (value.trim() === 'Homme' || value.trim() === 'Femme') return value.trim();
        return value;
    }

    onSubmit(): void {
        if (!this.isFormValid) {
            this.toastr.error('Veuillez remplir tous les champs obligatoires.', 'Erreur');
            return;
        }

        const civiliteNorm = this.normalizeCivilite(this.fournisseur.civilite);
        if (civiliteNorm !== 'Homme' && civiliteNorm !== 'Femme') {
            this.toastr.error('La civilité doit être Homme ou Femme.', 'Erreur');
            return;
        }

        const userId = this.authService.currentUserValue?.id || 0;
        // Payload aligné sur l’API (même structure que la page add-fournisseur)
        const payload: any = {
            civilite: civiliteNorm,
            nom_entreprise: this.fournisseur.nom_entreprise?.trim() || null,
            numero_identification_fiscale: this.fournisseur.numero_identification_fiscale?.trim() || null,
            identifiant_unique: this.fournisseur.identifiant_unique?.trim() || null,
            site_web: this.fournisseur.site_web?.trim() || null,
            station_id: this.stationId,
            user_id: userId,
            is_archive: false,
            emails: Array.isArray(this.fournisseur.emails)
                ? this.fournisseur.emails
                    .filter((e: any) => e && (e.email || (typeof e === 'string' && e)))
                    .map((e: any) => typeof e === 'string' ? e : e.email)
                : [],
            telephones: Array.isArray(this.fournisseur.telephones) ? this.fournisseur.telephones : [],
            adresses: Array.isArray(this.fournisseur.adresses) ? this.fournisseur.adresses : []
        };

        this.fournisseurService.addFournisseur(payload as Fournisseur).subscribe({
            next: (newFournisseur) => { // Récupérer la réponse de l'API
                this.modalRef?.close();
                this.fournisseurSaved.emit(newFournisseur); // Émettre le fournisseur ajouté
                this.toastr.success('Fournisseur ajouté avec succès !', '', {
                    positionClass: 'toast-bottom-right',
                    toastClass: 'toast ngx-toastr custom-success-toast'
                });
                this.resetForm();
            },
            error: (err) => {
                const body = err.error;
                let message = body?.message || body?.error || err.message || 'Erreur inconnue';
                if (body?.errors) {
                    if (Array.isArray(body.errors)) {
                        message = body.errors.map((e: any) => e.message || e.msg || e).join(', ');
                    } else if (typeof body.errors === 'object') {
                        const list: string[] = [];
                        for (const v of Object.values(body.errors) as any[]) {
                            list.push(...(Array.isArray(v) ? v : [v]));
                        }
                        message = list.join(', ');
                    }
                }
                this.toastr.error(message, 'Erreur lors de l’ajout du fournisseur');
            }
        });
    }

    private resetForm(): void {
        this.fournisseur = {
            civilite: '',
            nom_entreprise: '',
            numero_identification_fiscale: '',
            identifiant_unique: '',
            site_web: '',
            station_id: this.stationId,
            user_id: this.authService.currentUserValue?.id || 0,
            emails: [],
            telephones: [],
            adresses: [],
            is_archive:false,
        };
    }
}
